/**
 * PROJECT BOARD (PROJECT-MAP.md §13) — pure builder: project-map payload → a GLOBAL sequence of
 * vertical LANES (Jira-board style) covering the WHOLE project:
 *
 *   lane      = one execution stage: (milestone × Kahn wave), in project order M0 → Mn; each
 *               milestone closes with an EXIT lane (its exit criterion as the goal card).
 *   window    = the component shows exactly 4 lanes at a time; ‹Prev/Next› page by 2; milestone
 *               pills jump to a milestone's first lane. Default window = the ONGOING milestone.
 *   alignment = inside the window the DAG holds: producers sit in earlier lanes than consumers,
 *               and artifact edges are drawn between visible cards (exact + basename key match).
 *   unmet     = a require NOTHING in the plan provides → a red badge on the consumer card. Taken from
 *               the backend's plan-wide proof (``links.unmet`` — epics count as producers, L104); the
 *               leaf-only local recompute is a fallback for an older payload only.
 *
 * PURE (no React imports) so vitest pins lanes/window/edge semantics offline.
 */

export const VISIBLE_LANES = 4;
export const PAGE_STEP = 2;

const base = (k) => String(k || '').split('/').filter(Boolean).slice(-1)[0] || String(k || '');

function leavesOf(m) {
  const out = [];
  for (const e of (m?.epics || [])) {
    for (const t of (e.tasks || [])) out.push({ ...t, epicId: e.epicId, epicStory: e.story });
  }
  return out;
}

/** Kahn layering of one milestone's leaves over its intra-milestone artifact DAG (mirror of the
 *  server's derive_waves; used when the ledger waves are absent/uniform). */
function kahnWaves(leaves) {
  const prod = new Map();
  for (const t of leaves) {
    for (const k of (t.links?.provides || [])) {
      if (!prod.has(k)) prod.set(k, t.taskId);
      const b = base(k);
      if (!prod.has(b)) prod.set(b, t.taskId);
    }
  }
  const deps = new Map(leaves.map((t) => [t.taskId, new Set()]));
  for (const t of leaves) {
    for (const r of (t.links?.requires || [])) {
      const p = prod.get(r) ?? prod.get(base(r));
      if (p && p !== t.taskId) deps.get(t.taskId).add(p);
    }
  }
  const waves = new Map();
  const remaining = new Map(deps);
  let w = 0;
  while (remaining.size) {
    w += 1;
    const ready = [...remaining.keys()].filter(
      (id) => ![...remaining.get(id)].some((d) => remaining.has(d)));
    if (!ready.length) { for (const id of remaining.keys()) waves.set(id, w); break; }
    for (const id of ready) { waves.set(id, w); remaining.delete(id); }
  }
  return waves;
}

/** The ongoing milestone: first non-approved with tasks; else the last with tasks. */
export function focusMilestone(milestones) {
  const ms = Array.isArray(milestones) ? milestones : [];
  const withTasks = (m) => (m.epics || []).some((e) => (e.tasks || []).length);
  return ms.find((m) => m.status !== 'approved' && withTasks(m))
    || [...ms].reverse().find(withTasks) || null;
}

export function buildProjectLanes(data) {
  const milestones = (data && data.milestones) || [];

  // GLOBAL producer index (whole project) for unmet detection + edge drawing.
  const producers = new Map();      // key/basename → taskId
  for (const m of milestones) {
    for (const t of leavesOf(m)) {
      for (const k of (t.links?.provides || [])) {
        if (!producers.has(k)) producers.set(k, t.taskId);
        const b = base(k);
        if (!producers.has(b)) producers.set(b, t.taskId);
      }
    }
  }

  const lanes = [];
  const msIndex = [];
  const focus = focusMilestone(milestones);
  let defaultStart = 0;

  for (const m of milestones) {
    const leaves = leavesOf(m);
    if (!leaves.length) continue;
    const firstLane = lanes.length;
    if (m === focus) defaultStart = firstLane;

    const server = leaves.map((t) => Number(t.wave) || 0);
    const uniform = new Set(server).size <= 1;
    const local = uniform ? kahnWaves(leaves) : null;
    const waveOf = (t) => Math.max(1, (local ? local.get(t.taskId) : Number(t.wave)) || 1);

    const byWave = new Map();
    for (const t of leaves) {
      const w = waveOf(t);
      if (!byWave.has(w)) byWave.set(w, []);
      byWave.get(w).push(t);
    }
    for (const w of [...byWave.keys()].sort((a, b) => a - b)) {
      const items = byWave.get(w)
        .sort((a, b) => String(a.epicId).localeCompare(String(b.epicId))
          || String(a.taskId).localeCompare(String(b.taskId)))
        .map((t) => ({
          taskId: t.taskId,
          title: t.title,
          status: t.status,
          owner: (t.links?.assignedTo || [])[0] || '',
          rev: t.prov?.rev || 0,
          epicStory: t.epicStory || '',
          provides: (t.links?.provides || []),
          requires: (t.links?.requires || []),
          // UNMET — the BACKEND's plan-wide proof is the single source (L104: ``links.unmet``, epics
          // count as producers). The leaf-only local recompute is only a fallback for an older payload.
          unmet: Array.isArray(t.links?.unmet) ? t.links.unmet : (t.links?.requires || []).filter(
            (k) => !(producers.has(k) || producers.has(base(k)))),
        }));
      lanes.push({ id: `${m.id}:${w}`, kind: 'wave', milestoneId: m.id,
                   milestoneName: m.name, milestoneStatus: m.status, wave: w, items });
    }
    // EXIT lane — the milestone's goal card closes its lane run.
    lanes.push({ id: `${m.id}:exit`, kind: 'exit', milestoneId: m.id,
                 milestoneName: m.name, milestoneStatus: m.status,
                 exit: m.exit || '', items: [] });
    msIndex.push({ id: m.id, name: m.name, status: m.status,
                   firstLane, laneCount: lanes.length - firstLane });
  }

  // Clamp the default window so 4 lanes always fit.
  defaultStart = clampStart(defaultStart, lanes.length);
  return { lanes, milestones: msIndex, defaultStart };
}

export function clampStart(start, laneCount) {
  return Math.max(0, Math.min(Number(start) || 0, Math.max(0, laneCount - VISIBLE_LANES)));
}

/** Milestone ids intersecting the visible window (for pill highlighting). */
export function milestonesInWindow(lanes, start) {
  const seen = new Set();
  for (const l of lanes.slice(start, start + VISIBLE_LANES)) seen.add(l.milestoneId);
  return seen;
}

/** Edges among the VISIBLE window's cards: consumer.requires ↔ producer.provides, exact +
 *  basename. Cross-lane (incl. cross-milestone) as long as both endpoints are on the board. */
export function edgesForWindow(lanes, start) {
  const visible = lanes.slice(start, start + VISIBLE_LANES);
  const cards = visible.flatMap((l) => l.items);
  const prod = new Map();
  for (const c of cards) {
    for (const k of c.provides) {
      if (!prod.has(k)) prod.set(k, c.taskId);
      const b = base(k);
      if (!prod.has(b)) prod.set(b, c.taskId);
    }
  }
  const edges = [];
  for (const c of cards) {
    for (const k of c.requires) {
      const p = prod.get(k) ?? prod.get(base(k));
      if (p && p !== c.taskId) {
        edges.push({ id: `e:${p}->${c.taskId}:${base(k)}`, source: p, target: c.taskId,
                     label: base(k), data: { kind: 'artifact', key: k } });
      }
    }
  }
  // Terminal cards of a milestone whose EXIT lane is visible flow into the goal card.
  for (const l of visible) {
    if (l.kind !== 'exit') continue;
    const msCards = visible.filter((x) => x.milestoneId === l.milestoneId && x.kind === 'wave')
      .flatMap((x) => x.items);
    const consumedKeys = new Set();
    for (const c of msCards) for (const k of c.requires) { consumedKeys.add(k); consumedKeys.add(base(k)); }
    for (const c of msCards) {
      const terminal = c.provides.length
        && c.provides.every((k) => !consumedKeys.has(k) && !consumedKeys.has(base(k)));
      if (terminal) {
        edges.push({ id: `e:${c.taskId}->exit:${l.milestoneId}`, source: c.taskId,
                     target: `exit:${l.milestoneId}`, label: base(c.provides[0]),
                     data: { kind: 'terminal', key: c.provides[0] } });
      }
    }
  }
  return edges;
}

export function hasGraphContent(data) {
  return buildProjectLanes(data).lanes.some((l) => l.items.length || l.kind === 'exit');
}
